import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

import {
  CommonAreaType,
  ParkingSpotType,
  RoomType,
  SpaceDiscriminator,
  SpaceHierarchyReadModel,
  useGetSpacesByIdHierarchyQuery,
  usePostCommonAreasMutation,
  usePostParkingSpotsMutation,
  usePostRoomsMutation,
  usePostSpacesMutation,
} from '@client/api';
import { Button, Input, Modal, ModalContent, ModalFooter, Typography } from '@client/toolkit';

import { useGetGenericSpace } from '../../hooks/use-get-generic-space';

interface SpaceDuplicateModalProps {
  open: boolean;
  onClose: () => void;
  onAfterSubmit: () => void;
  spaceId: string | undefined;
  discriminator: SpaceDiscriminator | undefined;
  buildingId: string | undefined;
}

const MAX_COUNT = 100;
const PREVIEW_LIMIT = 5;

// Parse trailing digits from a name. Preserves prefix and zero-padding width.
// "P 1"  -> { prefix: "P ", number: 1, pad: 1 }
// "P01"  -> { prefix: "P",  number: 1, pad: 2 }
// "Foo"  -> null
function parseNumberedName(name: string): { prefix: string; number: number; pad: number } | null {
  const match = name.match(/^(.*?)(\d+)\s*$/);
  if (!match) return null;
  const prefix = match[1];
  const numStr = match[2];
  return { prefix, number: parseInt(numStr, 10), pad: numStr.length };
}

function findNodeInHierarchy(
  hierarchy: SpaceHierarchyReadModel | undefined,
  id: string,
): SpaceHierarchyReadModel | null {
  if (!hierarchy) return null;
  if (hierarchy.id === id) return hierarchy;
  for (const child of hierarchy.children ?? []) {
    const found = findNodeInHierarchy(child, id);
    if (found) return found;
  }
  return null;
}

function generateNames(
  currentName: string,
  siblingNames: string[],
  count: number,
): string[] {
  const existing = new Set(siblingNames);
  const parsed = parseNumberedName(currentName);
  const names: string[] = [];

  if (parsed) {
    const { prefix, pad } = parsed;
    let maxNumber = parsed.number;
    for (const siblingName of siblingNames) {
      const sib = parseNumberedName(siblingName);
      if (sib && sib.prefix === prefix && sib.number > maxNumber) {
        maxNumber = sib.number;
      }
    }

    let next = maxNumber + 1;
    for (let i = 0; i < count; i++) {
      let candidate = prefix + String(next).padStart(pad, '0');
      while (existing.has(candidate)) {
        next++;
        candidate = prefix + String(next).padStart(pad, '0');
      }
      names.push(candidate);
      existing.add(candidate);
      next++;
    }
    return names;
  }

  let idx = 1;
  for (let i = 0; i < count; i++) {
    let candidate = `${currentName} (kopi)`;
    while (existing.has(candidate)) {
      idx++;
      candidate = `${currentName} (kopi ${idx})`;
    }
    names.push(candidate);
    existing.add(candidate);
    idx++;
  }
  return names;
}

export function SpaceDuplicateModal({
  open,
  onClose,
  onAfterSubmit,
  spaceId,
  discriminator,
  buildingId,
}: SpaceDuplicateModalProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { data: space, isCommonArea, isRoom, isParkingSpot } = useGetGenericSpace(spaceId, discriminator);
  const { data: hierarchy } = useGetSpacesByIdHierarchyQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const [count, setCount] = useState<number>(1);

  const [mutation] = usePostSpacesMutation();
  const [commonAreaMutation] = usePostCommonAreasMutation();
  const [roomMutation] = usePostRoomsMutation();
  const [parkingSpotMutation] = usePostParkingSpotsMutation();

  const parentId = space?.parent?.id;

  const siblingNames = useMemo(() => {
    if (!parentId || !hierarchy) return [] as string[];
    const parentNode = findNodeInHierarchy(hierarchy, parentId);
    return (parentNode?.children ?? []).map((c) => c.name);
  }, [hierarchy, parentId]);

  const names = useMemo(() => {
    if (!space?.name) return [] as string[];
    const safeCount = Math.max(1, Math.min(MAX_COUNT, count || 1));
    return generateNames(space.name, siblingNames, safeCount);
  }, [count, space?.name, siblingNames]);

  useEffect(() => {
    if (!open) {
      setCount(1);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!space || !parentId || names.length === 0) return;

    const total = names.length;
    const toastId = toast.loading(tGlobal('space.duplicate.inProgress', { count: total }));

    const createOne = (name: string) => {
      const basePayload = {
        name,
        description: space.description,
        areaMetric: space.area?.metric,
        rentPriceId: space.rent?.id,
        assetClass: space.assetClass ?? undefined,
        parentId,
      };

      if (isCommonArea(space)) {
        return commonAreaMutation({
          body: {
            ...basePayload,
            type: space.type as CommonAreaType,
            operationalCostPriceId: space.operationalCost?.id,
          },
        }).unwrap();
      }
      if (isRoom(space)) {
        return roomMutation({
          body: { ...basePayload, type: space.type as RoomType },
        }).unwrap();
      }
      if (isParkingSpot(space)) {
        return parkingSpotMutation({
          body: { ...basePayload, type: space.type as ParkingSpotType },
        }).unwrap();
      }
      return mutation({
        body: { ...basePayload, discriminator: space.discriminator },
      }).unwrap();
    };

    // Close the modal immediately — the user can continue working while creates run in the background.
    onAfterSubmit();

    Promise.allSettled(names.map(createOne)).then((results) => {
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      if (failed === 0) {
        toast.success(tGlobal('space.duplicate.toastSuccess', { count: succeeded }), { id: toastId });
      } else if (succeeded === 0) {
        toast.error(tGlobal('space.duplicate.toastError'), { id: toastId });
      } else {
        toast.error(
          tGlobal('space.duplicate.toastPartial', { done: succeeded, total }),
          { id: toastId },
        );
      }
    });
  };

  const discriminatorLabel = useMemo(() => {
    const d = space?.discriminator ?? discriminator;
    return d ? tType(`SpaceDiscriminator.${d}`).toLowerCase() : tGlobal('entity.space');
  }, [discriminator, space?.discriminator, tGlobal, tType]);

  const title = tGlobal('space.duplicate.title', { entity: discriminatorLabel });

  const previewNames = names.slice(0, PREVIEW_LIMIT);
  const remainingCount = Math.max(0, names.length - PREVIEW_LIMIT);

  const canSubmit = !!space && !!parentId && names.length > 0 && count >= 1 && count <= MAX_COUNT;

  return (
    <Modal open={open} appear size="md" onClose={onClose} onBackdropClick={onClose} title={title}>
      <ModalContent className="space-y-5 px-4 py-5">
        <Typography variant="secondary">{tGlobal('space.duplicate.description')}</Typography>

        <div className="space-y-2">
          <Typography variant="label">{tGlobal('space.duplicate.count')}</Typography>
          <Input
            type="number"
            min={1}
            max={MAX_COUNT}
            value={count}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isNaN(v)) setCount(1);
              else setCount(Math.max(1, Math.min(MAX_COUNT, v)));
            }}
          />
          <Typography variant="secondary" className="text-white/50 text-xs">
            {tGlobal('space.duplicate.countHint')}
          </Typography>
        </div>

        {previewNames.length > 0 && (
          <div className="space-y-2">
            <Typography variant="label">{tGlobal('space.duplicate.preview')}</Typography>
            <div className="text-sm text-white/90 flex flex-wrap gap-2">
              {previewNames.map((n) => (
                <span key={n} className="px-2 py-1 rounded bg-brand-primary/20">
                  {n}
                </span>
              ))}
              {remainingCount > 0 && (
                <span className="px-2 py-1 text-white/60">
                  {tGlobal('space.duplicate.previewMore', { count: remainingCount })}
                </span>
              )}
            </div>
          </div>
        )}
      </ModalContent>

      <ModalFooter>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {tGlobal('space.duplicate.submit')}
        </Button>
        <Button color="primary-dark" onClick={onClose}>
          {tCommon('cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
